<template>
  <v-container>
    <MessagePopup :text="errorMessage" color="error"/>
    <v-data-table
      :headers="headers"
      :items="suppliers"
      :loading="loadingSuppliers"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-spacer></v-spacer>
            <SupplierAddDialog
              @refresh="getSuppliers"
            />
            <DocSuppliersList/>
        </v-toolbar>
        <v-alert type="warning" text dense class="ma-4" @click="getSuppliers" prominent outlined v-if="needAuthentication > 0">
          <v-row align="center">
            <v-col class="grow">
              {{ needAuthentication }} supplier(s) has not been authenticated. Click the orange
              lock(s) to be redirected to the authentication page, then click refresh button.
            </v-col>
            <v-col class="shrink">
              <v-btn @click="getSuppliers" color="warning">
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </template>
      <template v-slot:item.action="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="warning"
              :to="{ name: 'SuppliersConsole', params: { id: item.id } }"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small>mdi-console-line</v-icon>
            </v-btn>
          </template>
          <span>Dev console</span>
        </v-tooltip>

        <SupplierEditDialog
          :item="item"
          @refresh="getSuppliers"
        />
        <v-btn icon small color="error" @click="deleteItem(item)">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.enabled="{ item }">
        <v-btn v-if="item.enabled" icon small @click="enableSupplier(item['@id'], false)">
          <v-icon small>mdi-checkbox-marked</v-icon>
        </v-btn>
        <v-btn v-else icon small @click="enableSupplier(item['@id'], true)">
          <v-icon small>mdi-checkbox-blank-outline</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.connector="{ item }">
        {{ item.connector }}
        <v-btn icon small :color="item.authenticated ? 'success' : 'warning'" v-if="item.authenticationURL" :href="$server + item.authenticationURL + '?supplier=' + item.id" target="_blank">
          <v-icon small v-if="item.authenticated">mdi-lock</v-icon>
          <v-icon small v-else>mdi-lock-open-variant</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.capabilities="{ item }">
        <v-tooltip top v-if="isSupplierCapableOf({ connectorName: item.connector, capability: 'searchArticle' })">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
            >mdi-magnify</v-icon>
          </template>
          <span>Search articles</span>
        </v-tooltip>
        <v-tooltip top v-if="isSupplierCapableOf({ connectorName: item.connector, capability: 'refreshArticle' })">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
            >mdi-refresh</v-icon>
          </template>
          <span>Refresh articles</span>
        </v-tooltip>
        <v-tooltip top v-if="isSupplierCapableOf({ connectorName: item.connector, capability: 'orderHistory' })">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
            >mdi-cart-arrow-right</v-icon>
          </template>
          <span>Order history</span>
        </v-tooltip>
        <v-tooltip top v-if="isSupplierCapableOf({ connectorName: item.connector, capability: 'supplyChain' })">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
            >mdi-tray-full</v-icon>
          </template>
          <span>Supply-Chain program</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import SupplierAddDialog from '@/components/SupplierAddDialog.vue'
import SupplierEditDialog from '@/components/SupplierEditDialog.vue'
import MessagePopup from '@/components/MessagePopup.vue'
import DocSuppliersList from '@/components/DocSuppliersList.vue'
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  data: () => ({
    headers: [
      { text: 'Enabled', value: 'enabled' },
      { text: 'Name', value: 'name' },
      { text: 'Country', value: 'country' },
      { text: 'Currency', value: 'currency' },
      { text: 'Ex Rate', value: 'exchangeRate' },
      { text: 'Connector', value: 'connector' },
      { text: 'Capabilities', sortable: false, value: 'capabilities', align: 'center' },
      { text: 'Action', sortable: false, value: 'action', align: 'center' }
    ],
    errorMessage: ''
  }),
  components: {
    SupplierAddDialog,
    SupplierEditDialog,
    MessagePopup,
    DocSuppliersList
  },
  computed: {
    ...mapState(['suppliers', 'loadingSuppliers', 'connectors']),
    ...mapGetters(['isSupplierCapableOf']),
    needAuthentication () {
      return this.suppliers.filter(item => (item.authenticationURL && !item.authenticated)).length
    }
  },
  watch: {
    options: {
      handler () {
        this.getData()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['getSuppliers']),
    enableSupplier (target, value) {
      this.$http.patch(target,
        { enabled: value },
        { headers: { 'Content-Type': 'application/merge-patch+json' } }
      )
        .then(() => {
          this.getSuppliers()
        })
        .catch(error => {
          if (!error.response) {
            this.errorMessage = error.message
          } else {
            this.errorMessage = error.response.data['hydra:description'].replace(/\n/g, '<br/>')
          }
        })
    },
    deleteItem (item) {
      if (confirm('Do you really want to remove ' + item.name)) {
        this.$http.delete(item['@id'])
          .finally(() => {
            this.getSuppliers()
          })
      }
    }
  }
}
</script>
